import { useState, useMemo, useEffect, useRef, useDeferredValue } from 'react';
import { useCategories, useItemsInCategory, useFeaturedItems, useAllItems } from './hooks';
import type { CatalogCategory, CatalogItem } from './hooks';
import type { CatalogItemUnit } from '@matix/types';
import { parseEmbeddedBarcode } from '@matix/utils';
import { useCart } from '../../store/cart';
import { useUI } from '../../store/ui';
import { useTenantConfig } from '../../store/tenantConfig';
import { useInvoiceSources } from '../../store/invoiceSources';
import { useCustomerPicker } from '../../store/customers';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { SearchIcon, BarcodeIcon, UserIcon, PackageIcon, ArrowRightIcon, ChevronRightIcon } from '../../components/icons';
import { useLang } from '../../lib/language';
import { namePair, useLocalizedName } from '../../lib/localizedName';
import { isFallbackPrice, isMultiUnit, posUnitsOf, priceForTier, sellingUnitOf } from '../../lib/priceTier';
import { BarcodeScannerModal } from '../scanner/BarcodeScannerModal';
import type { ScanLookup, ScannedItem } from '../scanner/BarcodeScannerModal';


/**
 * Catalog screen — left side of the workspace.
 *
 * Layout matches v6 prototype:
 *   - Search bar (search input + barcode icon + Customer button)
 *   - Horizontal category chips with emoji + count
 *   - Product grid: tiles with colored emoji block + name + name_ar + price
 *   - COMBO badge for combo items (border highlight)
 *
 * Data comes from the live pos-api catalog. We map CatalogItem (which has
 * sparse fields — many tenants have no images or prices) to the prototype's
 * MockProduct shape, falling back to category emojis when needed.
 */

// Category-name → emoji + bg color lookup. Falls through to a default tile
// look. Words match either English or Arabic name fragments; case-insensitive.
const CATEGORY_VISUALS: Array<{ match: RegExp; icon: string; bg: string }> = [
  { match: /food|meal|طعام|وجب/i, icon: '🍽️', bg: '#FEF3C7' },
  { match: /salad|سلطة/i, icon: '🥗', bg: '#D1FAE5' },
  { match: /shawarma|شاورما/i, icon: '🌯', bg: '#FED7AA' },
  { match: /burger|برجر/i, icon: '🍔', bg: '#FEF3C7' },
  { match: /pizza|بيتزا/i, icon: '🍕', bg: '#FEE2E2' },
  { match: /chicken|دجاج/i, icon: '🍗', bg: '#FEF3C7' },
  { match: /beef|meat|لحم|لحوم/i, icon: '🥩', bg: '#FEE2E2' },
  { match: /side|fries|بطاطس/i, icon: '🍟', bg: '#FEF3C7' },
  { match: /drink|juice|coffee|عصير|قهوة|مشروب/i, icon: '🥤', bg: '#DBEAFE' },
  { match: /dessert|cake|cheesecake|كيك|حلو/i, icon: '🍰', bg: '#FCE7F3' },
  { match: /chocolate|شوكول/i, icon: '🍫', bg: '#FED7AA' },
  { match: /waffle|وافل/i, icon: '🧇', bg: '#FEF3C7' },
  { match: /coctail|cocktail|كوكتيل/i, icon: '🍹', bg: '#FEE2E2' },
  { match: /extra|إضاف/i, icon: '➕', bg: '#EDF1F5' },
];
// Empty icon = no category-specific glyph matched. Renders the generic
// PackageIcon placeholder instead of a food emoji, so non-restaurant tenants
// (retail, clothing, etc.) get a neutral product icon.
const DEFAULT_VISUAL = { icon: '', bg: '#EDF1F5' };

function categoryVisual(c: CatalogCategory) {
  const text = `${c.name ?? ''} ${c.name_ar ?? ''}`;
  return CATEGORY_VISUALS.find((v) => v.match.test(text)) ?? DEFAULT_VISUAL;
}

/** Map a live CatalogItem to a MockProduct-shape so the prototype tile renders. */
function mapItemForTile(
  item: CatalogItem,
  parentVisual: { icon: string; bg: string },
  tier: 1 | 2 | 3 | 4 | null,
  /** The unit this tile sells (a multi-unit item has one tile per unit). */
  unit: CatalogItemUnit | null = null,
) {
  return {
    id: item.id,
    name: item.name ?? `#${item.code}`,
    nameAr: item.name_ar ?? '',
    price: priceForTier(item, tier, unit),
    cat: 'live',
    emoji: parentVisual.icon,
    bg: parentVisual.bg,
    station: '',
    mods: undefined as string[] | undefined,
    combo: false,
    tag: undefined as string | undefined,
  };
}

/**
 * The barcode to carry onto a cart line for receipt printing: the selling
 * unit's barcode when known, else the first unit that has one. Null when the
 * item has no barcode on any unit.
 */
function itemBarcode(item: CatalogItem, unit?: CatalogItemUnit | null): string | null {
  if (unit?.barcode) return unit.barcode;
  const selling = item.units.find((u) => u.unit_id === item.selling_unit_id);
  if (selling?.barcode) return selling.barcode;
  return item.units.find((u) => u.barcode)?.barcode ?? item.barcodes?.[0] ?? null;
}

/**
 * The unit whose barcode (or, failing that, whose item code) a scan hit — so
 * a carton's barcode rings up a carton and a can's a can. Null when the code
 * matched the item but no unit in particular.
 */
function scannedUnitOf(item: CatalogItem, rawQuery: string): CatalogItemUnit | null {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return null;
  return item.units.find((u) => u.barcode?.toLowerCase() === q) ?? null;
}

/**
 * Does this item match the search query? Matches across name, Arabic name,
 * code, and every unit barcode. English/code/barcode are matched case-
 * insensitively as substrings; Arabic is matched on the raw trimmed text.
 */
function itemMatchesSearch(item: CatalogItem, rawQuery: string, fullMatch = false): boolean {
  const ar = rawQuery.trim();
  const q = ar.toLowerCase();
  if (!q) return false;

  // FullBarcodeScan — legacy swaps every `indexOf` in its item filter for `===`
  // (order_events_definitions.js:2710-2725). Shops that scan long barcodes turn
  // it on so a scan cannot pull in every item whose name merely contains those
  // digits.
  if (fullMatch) {
    if (item.name?.toLowerCase() === q) return true;
    if (item.name_ar === ar) return true;
    if (item.code.toLowerCase() === q) return true;
    if (item.barcodes?.some((b) => b.toLowerCase() === q)) return true;
    return item.units.some((u) => u.barcode?.toLowerCase() === q);
  }

  if (item.name?.toLowerCase().includes(q)) return true;
  if (item.name_ar?.includes(ar)) return true;
  if (item.code.toLowerCase().includes(q)) return true;
  // Extra item-level barcodes (Inv_Items_Barcode) — the legacy till scans them.
  if (item.barcodes?.some((b) => b.toLowerCase().includes(q))) return true;
  return item.units.some((u) => u.barcode?.toLowerCase().includes(q));
}

/**
 * The settle window once the box was filled at MACHINE speed — every gap
 * between characters under SCANNER_GAP_MS. A wedge scanner with no Enter
 * suffix (the scale labels at AlSaqi) sends a whole code a few ms per
 * character and never stops for this long in the middle of one, so it is
 * resolved this long after its last character.
 *
 * Only a machine-speed fill auto-resolves. A query typed BY HAND waits for
 * Enter (2026-10-11): on a tenant with short codes (GL_WEB_boulevardkw — 1,
 * 12, 123 are all items) any pause after "1" rang up item 1 before the next
 * digit arrived, and a longer pause only moved the problem.
 */
const SCANNER_SETTLE_MS = 80;
const SCANNER_GAP_MS = 35;

/**
 * Most tiles the search grid draws. A scan types its code one character at a
 * time, and on a tenant with zero-padded codes the first "0" matches almost
 * the whole catalogue — drawing ~2,800 tiles (photos included) per keystroke
 * is what made a scale label slow to ring up. Matching is still done over
 * every item; only the drawing stops here.
 */
const SEARCH_RENDER_CAP = 60;

const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

/**
 * Exact barcode/code hit — the scanner case. A scanned barcode (or a fully
 * typed code) should resolve to one item even if it's a substring of others'
 * names. Used to auto-add immediately rather than waiting for the result list
 * to narrow to one.
 */
function itemExactScanMatch(item: CatalogItem, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return false;
  if (item.code.toLowerCase() === q) return true;
  if (item.barcodes?.some((b) => b.toLowerCase() === q)) return true;
  return item.units.some((u) => u.barcode?.toLowerCase() === q);
}

export function CatalogScreen() {
  const { t, lang } = useLang();
  // Category names come from the tenant's own data, so they are not `t({en,ar})`
  // strings — they are two columns on the row, and the active language picks
  // which one is shown (falling back to the other when a tenant filled in only
  // one). Used for every category label on this screen.
  const catName = useLocalizedName();
  // The category whose items we are currently viewing. Null until cashier
  // taps a leaf (categories with children drill-in instead of fetching items).
  const [activeLeafId, setActiveLeafId] = useState<number | null>(null);

  // Stack of parent categories the cashier has drilled into. Empty = top level.
  const [parentStack, setParentStack] = useState<CatalogCategory[]>([]);

  const [search, setSearch] = useState('');

  // Camera scanner (the barcode button). Separate from `openModal` because it
  // is owned entirely by this screen and never stacks with the cart modals.
  const [scannerOpen, setScannerOpen] = useState(false);

  const showModal = useUI((s) => s.showModal);

  // Admin flag (mstr_ConfigurationParameter → IsShowProductImage). When off,
  // tiles fall back to the category emoji / placeholder even if a photo exists.
  const showProductImages = useTenantConfig((s) => s.showProductImages);
  // FullBarcodeScan — whole-value matching instead of substring. See
  // itemMatchesSearch; legacy applies it to the same item filter.
  const fullBarcodeScan = useTenantConfig((s) => s.fullBarcodeScan);
  // CombineQtyOnItemScan — a repeat scan bumps the existing line.
  const combineQtyOnScan = useTenantConfig((s) => s.combineQtyOnScan);
  // PosBarcodeScanerConfiguration — a weighing shop's own label carries the
  // item code AND the weight. See embeddedScan below.
  const barcodeScanner = useTenantConfig((s) => s.barcodeScanner);

  // ── Invoice source (price list) ──────────────────────────────────────────
  // Empty on tenants that don't run multiple price lists, in which case `tier`
  // is null and every price below resolves to the server's `selling_price` —
  // i.e. exactly what this screen did before the feature existed.
  const sources = useInvoiceSources((s) => s.sources);
  const activeSourceId = useInvoiceSources((s) => s.activeId);
  const sourceLocked = useInvoiceSources((s) => s.locked);
  const tier = useInvoiceSources((s) =>
    s.sources.find((x) => x.id === s.activeId)?.price_tier ?? null,
  );

  // Single source of truth — all visible categories for the branch.
  const allCatsQuery = useCategories();
  const allCats = allCatsQuery.data ?? [];

  // Fetch items only when a leaf is selected.
  const itemsQuery = useItemsInCategory(activeLeafId);

  // Admin-flagged featured items — shown on the landing page (top level, no
  // active leaf, no search), mirroring the legacy Matix POS featured grid.
  const featuredItems = useFeaturedItems();

  // Every item in the catalog — backs global search (across all categories).
  const allItems = useAllItems();

  // Build the categories visible at the current drill level.
  const currentParentId = parentStack.length === 0 ? null : parentStack[parentStack.length - 1]!.id;
  const visibleCategories = useMemo(
    () => allCats.filter((c) => c.parent_id === currentParentId),
    [allCats, currentParentId],
  );

  // Helper: does this category have any children?
  const hasChildren = (catId: number) => allCats.some((c) => c.parent_id === catId);

  const currentParent = parentStack[parentStack.length - 1] ?? null;
  const activeLeaf = useMemo(
    () => allCats.find((c) => c.id === activeLeafId) ?? null,
    [allCats, activeLeafId],
  );
  const activeVisual = activeLeaf ? categoryVisual(activeLeaf) : DEFAULT_VISUAL;

  // Resolve an item's visual + station from its first known category. Used for
  // featured tiles, which aren't browsed under one active category so they have
  // no `activeVisual`/`activeLeaf` to inherit from.
  const itemContext = (item: CatalogItem) => {
    const cat = allCats.find((c) => item.category_ids.includes(c.id)) ?? null;
    return {
      visual: cat ? categoryVisual(cat) : DEFAULT_VISUAL,
      station: cat ? catName(cat) : '',
    };
  };
  // The category the cashier is currently browsing — surfaced on each cart line
  // (replaces the old hard-coded "Hot kitchen" station label). Copied onto the
  // line when the item is tapped, so lines already in the basket keep the label
  // they were added with if the cashier flips language mid-sale.
  const activeCategoryName = activeLeaf ? catName(activeLeaf) : '';

  const isSearching = search.trim().length > 0;

  // Items shown when browsing a category (no local filter — search is global).
  const categoryItems = itemsQuery.data ?? [];

  // Global search — matches name / code / barcode across ALL categories, so the
  // cashier doesn't have to drill into a category first. Sorted by name.
  // Sorted ONCE per catalogue load; a filter keeps the order, so a keystroke
  // no longer re-sorts thousands of names.
  const itemsByName = useMemo(
    () => [...allItems].sort((a, b) => nameCollator.compare(a.name ?? '', b.name ?? '')),
    [allItems],
  );
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const matches = itemsByName.filter((it) => itemMatchesSearch(it, search, fullBarcodeScan));
    // A query that IS an item's code or a unit barcode means that item: show
    // only the exact hits, not every item whose barcode merely contains the
    // digits (Ponytail_test 2026-09-29: "040019" also listed DIA LIGHT CLEAR,
    // barcode 3474630400191). A multi-unit exact hit now waits for the
    // cashier to tap a unit, so the extra tile was on screen to be tapped.
    const exact = matches.filter((it) => itemExactScanMatch(it, search));
    return exact.length > 0 ? exact : matches;
  }, [itemsByName, search, isSearching, fullBarcodeScan]);
  // The grid draws a deferred copy, so the box keeps up with the scanner and
  // React may skip drawing the intermediate prefixes altogether.
  const shownResults = useDeferredValue(searchResults);
  const shownQuery = useDeferredValue(search);

  // Show the featured grid on the landing page only: top level (no parent
  // drilled, no leaf selected) and no active search.
  const showFeatured =
    activeLeafId === null &&
    parentStack.length === 0 &&
    !isSearching &&
    featuredItems.length > 0;

  const addLine = useCart((s) => s.addLine);
  const cartLines = useCart((s) => s.lines);
  const customer = useCart((s) => s.customer);

  /** Cart shape for an item, given the visual context it was picked from. */
  function cartProductFor(
    item: CatalogItem,
    ctx: { visual: { icon: string; bg: string }; station: string },
    /** The unit tile / barcode the cashier picked; null = the selling unit. */
    unit: CatalogItemUnit | null = null,
  ) {
    const lineUnit = unit ?? sellingUnitOf(item) ?? null;
    // The unit is NAMED on the line only for a multi-unit item, so a tenant
    // whose items sell in one unit sees the cart and the receipt unchanged.
    const named = isMultiUnit(item) && lineUnit;
    return {
      id: item.id,
      name: item.name ?? `#${item.code}`,
      nameAr: item.name_ar ?? '',
      price: priceForTier(item, tier, unit),
      unitId: lineUnit?.unit_id ?? null,
      ...(named
        ? { unitName: lineUnit.unit_name ?? null, unitNameAr: lineUnit.unit_name_ar ?? null }
        : {}),
      cat: 'live',
      emoji: ctx.visual.icon,
      bg: ctx.visual.bg,
      // Carry the item's own photo onto the line so the cart thumbnail matches
      // the tile the cashier just tapped. `emoji`/`bg` above are resolved from
      // the CATEGORY, so without this every line in a category looks the same.
      image: showProductImages ? item.image_data ?? null : null,
      station: ctx.station,
      code: item.code ?? null,
      barcode: itemBarcode(item, unit),
      restockOnReturn: item.restock_on_return,
      allowDecimals: item.allow_decimals,
    };
  }

  function handleProductClick(
    item: CatalogItem,
    override?: { visual: { icon: string; bg: string }; station: string },
    /** True only on the scan path — see CombineQtyOnItemScan. */
    fromScan = false,
    /** The unit tile tapped (multi-unit item); null = the selling unit. */
    unit: CatalogItemUnit | null = null,
    /** Decoded off an embedded barcode; every other way in adds one. */
    qty = 1,
  ) {
    const ctx = {
      visual: override?.visual ?? activeVisual,
      station: override?.station ?? activeCategoryName,
    };
    if (item.modifier_groups && item.modifier_groups.length > 0) {
      showModal('modifier', {
        product: item,
        meta: {
          station: ctx.station,
          emoji: ctx.visual.icon,
          bg: ctx.visual.bg,
          image: showProductImages ? item.image_data ?? null : null,
          unit,
          qty,
        },
      });
      return;
    }
    addLine(cartProductFor(item, ctx, unit), [], qty, undefined, fromScan && combineQtyOnScan);
    toast(`${t({ en: 'Added', ar: 'تمت إضافة' })} ${item.name ?? item.code}`, 'success');
  }

  /**
   * Resolve a scan through the tenant's own embedded-barcode layout, when it
   * has one and this scan fits it.
   *
   * A shop that weighs its goods prints a label carrying both the item code
   * and the weight — 008131 03500 is 3.5 kg of item 008131. The layout says
   * where each field sits; `parseEmbeddedBarcode` (in @matix/utils, with the
   * arithmetic and its tests) does the splitting, and returns null for a scan
   * that is not one of these — an ordinary EAN-13 off a supplier's pack, a
   * typed item code, a product name. Null here means "carry on as before",
   * which is why every existing path below is unchanged.
   *
   * A parsed code whose item is NOT in the catalog also returns null rather
   * than reporting a miss: the raw digits then go through the ordinary search,
   * which is the legacy behaviour and the only thing that could still match
   * (an item whose real barcode happens to be that long).
   */
  function embeddedScan(raw: string): { item: CatalogItem; qty: number } | null {
    const parsed = parseEmbeddedBarcode(raw, barcodeScanner);
    if (!parsed) return null;
    const code = parsed.itemCode.toLowerCase();
    // Matched literally, zero padding included — Matix pads item codes to a
    // fixed width, so the padding in the label IS the padding in the table.
    const item = allItems.find((it) => it.code.trim().toLowerCase() === code);
    return item ? { item, qty: parsed.qty } : null;
  }

  /**
   * Resolve a decoded barcode for the scanner modal. Pure lookup — the modal
   * asks for a quantity before anything reaches the cart. The one exception is
   * an item with modifiers: its own modal cannot sit on top of a live camera,
   * so the scanner hands the screen over ('handled') and closes.
   */
  function scanLookup(code: string): ScanLookup {
    // An embedded barcode resolves to an item AND a weight. The modal opens on
    // that weight rather than on 1, and the cashier can still change it — the
    // label is printed off a scale and the camera may have read it at an
    // angle, so this is the one place a decoded quantity gets a second look.
    const embedded = embeddedScan(code);
    if (embedded) {
      // The unit is the selling unit: the label carries an ITEM code, not a
      // unit barcode, so there is no particular unit for it to name.
      const resolved = scannedItemFor(embedded.item, null, embedded.qty);
      return resolved.status === 'found' ? { ...resolved, qty: embedded.qty } : resolved;
    }

    const target = allItems.find((it) => itemExactScanMatch(it, code));
    if (!target) {
      // No exact hit. Say so; the modal offers "Search this code", which drops
      // the raw code into the search box for the near matches.
      const partial = allItems.some((it) => itemMatchesSearch(it, code, fullBarcodeScan));
      return {
        status: 'not-found',
        message: partial
          ? t({
              en: 'No exact match, but some items look similar — search it instead.',
              ar: 'لا يوجد تطابق تام، لكن هناك أصناف مشابهة — جرّب البحث عنه.',
            })
          : '',
      };
    }

    // A carton's barcode rings up a carton, a can's a can. A code that matched
    // the item itself (its item code) falls back to the selling unit.
    return scannedItemFor(target, scannedUnitOf(target, code));
  }

  /**
   * The scanner modal's view of one resolved item, or a hand-over when it has
   * modifiers — its own modal cannot sit on top of a live camera.
   *
   * `unit` is the unit whose barcode was scanned; null falls back to the
   * selling unit. `qty` only matters on the hand-over — it is the weight an
   * embedded barcode carried, which the modifier modal must not drop.
   */
  function scannedItemFor(
    target: CatalogItem,
    unit: CatalogItemUnit | null,
    qty = 1,
  ): ScanLookup {
    const lineUnit = unit ?? sellingUnitOf(target) ?? null;
    const multi = isMultiUnit(target);

    if (target.modifier_groups && target.modifier_groups.length > 0) {
      handleProductClick(target, itemContext(target), true, unit, qty);
      return { status: 'handled' };
    }

    const inCart = cartLines
      .filter(
        (l) =>
          l.product.id === target.id &&
          (l.product.unitId ?? null) === (lineUnit?.unit_id ?? null),
      )
      .reduce((sum, l) => sum + l.qty, 0);

    return {
      status: 'found',
      item: {
        id: target.id,
        name: target.name ?? `#${target.code}`,
        nameAr: target.name_ar ?? null,
        price: priceForTier(target, tier, unit),
        code: target.code ?? null,
        image: showProductImages ? target.image_data ?? null : null,
        inCart,
        allowDecimals: target.allow_decimals,
        unitId: lineUnit?.unit_id ?? null,
        ...(multi && lineUnit
          ? { unitName: lineUnit.unit_name ?? null, unitNameAr: lineUnit.unit_name_ar ?? null }
          : {}),
      },
    };
  }

  /** Commit the quantity chosen in the scanner. */
  function handleScanAdd(scanned: ScannedItem, qty: number) {
    const target = allItems.find((it) => it.id === scanned.id);
    if (!target) return;
    const unit =
      scanned.unitId != null
        ? target.units.find((u) => u.unit_id === scanned.unitId) ?? null
        : null;
    addLine(cartProductFor(target, itemContext(target), unit), [], qty, undefined, combineQtyOnScan);
  }

  /**
   * Single-click behavior — matches the legacy Matix POS:
   *   - Category has children → drill into them (replace the visible row)
   *   - Category is a leaf → fetch its items
   */
  function handleCategoryClick(c: CatalogCategory) {
    if (hasChildren(c.id)) {
      // Drill in. Clear any active leaf — we're now browsing.
      setParentStack((s) => [...s, c]);
      setActiveLeafId(null);
    } else {
      // It's a leaf. Show its items.
      setActiveLeafId(c.id);
    }
  }

  function goBackUpStack() {
    setParentStack((s) => s.slice(0, -1));
    setActiveLeafId(null);
  }

  // Auto-add on a unique match. When the query resolves to exactly one item —
  // either an exact barcode/code hit (the scanner case) or a single search
  // result — add it straight to the cart and clear the box, matching the legacy
  // POS. The guard ref keeps a settled query from re-firing every render; it
  // resets when the box is cleared, so retyping the same term adds again.
  //
  // ── Why this waits ───────────────────────────────────────────────────────
  // A hardware (wedge) scanner types the barcode one character at a time,
  // tens of milliseconds apart. Resolving on every keystroke added the item
  // TWICE: the moment a prefix of the barcode narrowed the search to one item
  // it was added and the box cleared, then the scanner's remaining digits
  // landed as a fresh query, matched the same barcode as a substring, and
  // added it again — in the selling unit both times, whatever barcode was
  // scanned. A machine-speed fill now resolves SCANNER_SETTLE_MS after its
  // last character (a scanner never pauses that long mid-code); a hand-typed
  // query is resolved only by Enter/Tab, which most scanners also send. The
  // unit whose barcode matched is the unit that rings up.
  const autoAddedFor = useRef('');
  /** Keystroke timing of the current query — see SCANNER_SETTLE_MS. */
  const typing = useRef({ at: 0, len: 0, machine: true });

  /** Resolve the search box to a cart line now. True when something was added. */
  function commitSearch(): boolean {
    const q = search.trim();
    if (!q || q === autoAddedFor.current) return false;
    // An embedded barcode wins outright: it is this tenant's own label, and
    // its digits must never be treated as a search term.
    const embedded = embeddedScan(q);
    if (embedded) {
      autoAddedFor.current = q;
      handleProductClick(embedded.item, itemContext(embedded.item), true, null, embedded.qty);
      setSearch('');
      return true;
    }
    // Prefer an exact barcode/code match (resolves even when the term is a
    // substring of other items' names); otherwise a lone search result.
    const target =
      allItems.find((it) => itemExactScanMatch(it, q)) ??
      (searchResults.length === 1 ? searchResults[0]! : null);
    if (!target) return false;
    const unit = scannedUnitOf(target, q);
    // A multi-unit item is only added outright when the query named ONE unit
    // (its barcode). The item code or a name matches the item, not a unit —
    // adding it would silently pick the selling unit (Ponytail_test
    // 2026-09-29: code 040019 rang up "Pcs" when the cashier wanted the BOX).
    // Leave the results up instead: they show one tile per unit to tap.
    if (unit == null && isMultiUnit(target)) {
      autoAddedFor.current = q;
      return false;
    }
    autoAddedFor.current = q;
    handleProductClick(target, itemContext(target), true, unit);
    setSearch('');
    return true;
  }

  useEffect(() => {
    const q = search.trim();
    if (!q) {
      autoAddedFor.current = '';
      return;
    }
    if (q === autoAddedFor.current) return;
    // Hand-typed (or pasted) → nothing until Enter. Only a scanner burst
    // resolves on its own.
    if (!(typing.current.machine && q.length > 1)) return;
    const timer = setTimeout(commitSearch, SCANNER_SETTLE_MS);
    return () => clearTimeout(timer);
    // commitSearch closes over the latest query + data; the effect is keyed on
    // those, not on the closure identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchResults, allItems]);

  // One product tile. Shared by the featured grid, global search results, and
  // category browsing so they stay visually identical.
  /**
   * Switch price list. Re-prices the grid (via `tier`) AND the open cart, the
   * same way the legacy click handler does — a cashier who has already rung up
   * three items and then realises it is a delivery order must not have to
   * clear the cart.
   *
   * Lines whose item is no longer in the catalog keep their price; see
   * `repriceLinesForTier`.
   */
  function pickSource(id: number) {
    if (sourceLocked) return;
    const next = sources.find((s) => s.id === id);
    if (!next || next.id === activeSourceId) return;

    useInvoiceSources.getState().setActive(id);

    const byId = new Map(allItems.map((it) => [it.id, it]));
    useCart.getState().repriceLinesForTier((productId, unitId) => {
      const it = byId.get(productId);
      if (!it) return null;
      // Re-price in the unit the line was rung up in: a carton line keeps
      // reading the carton row's tier, never the piece's.
      const unit = unitId != null ? it.units.find((u) => u.unit_id === unitId) ?? null : null;
      return priceForTier(it, next.price_tier, unit && isMultiUnit(it) ? unit : null);
    });
  }

  const renderTile = (
    item: CatalogItem,
    ctx: { visual: { icon: string; bg: string }; station: string },
    keyPrefix = '',
  ) => {
    // A multi-unit item gets one tile per POS unit — "Piece 0.150" beside
    // "CTN 2.750", the way the legacy POS laid it out — each ringing up that
    // unit. A single-unit item is the one tile it always was, unit unnamed.
    const units = posUnitsOf(item);
    if (units.length > 1) {
      return units.map((unit) => renderUnitTile(item, ctx, keyPrefix, unit));
    }
    return renderUnitTile(item, ctx, keyPrefix, null);
  };

  const renderUnitTile = (
    item: CatalogItem,
    ctx: { visual: { icon: string; bg: string }; station: string },
    keyPrefix: string,
    unit: CatalogItemUnit | null,
  ) => {
    const p = mapItemForTile(item, ctx.visual, tier, unit);
    // Priced off the default tier because this source's tier is blank for the
    // item. Marked so the cashier can see it, since the fallback is otherwise
    // indistinguishable from a correctly-tiered price.
    const fellBack = isFallbackPrice(item, tier, unit);
    const unitLabel = unit
      ? (lang === 'ar' ? unit.unit_name_ar : unit.unit_name) || unit.unit_name || unit.unit_name_ar
      : null;
    return (
      <div
        key={`${keyPrefix}${p.id}${unit ? `-u${unit.unit_id}` : ''}`}
        className="product"
        onClick={() => handleProductClick(item, ctx, false, unit)}
      >
        {showProductImages && item.image_data ? (
          <div
            className="product-image product-image--photo"
            style={{ backgroundImage: `url(${item.image_data})` }}
          />
        ) : (
          <div className="product-image" style={{ background: p.bg }}>
            {p.emoji ? p.emoji : <PackageIcon size={40} style={{ color: 'var(--text-muted)' }} />}
          </div>
        )}
        <div className="product-info">
          {(() => {
            // The till's language leads, bold; the other script sits under it.
            const n = namePair(lang, { name: p.name, name_ar: p.nameAr });
            return (
              <>
                <div className="product-name" dir="auto">{n.primary}</div>
                {n.secondary && <div className="product-name-ar" dir="auto">{n.secondary}</div>}
              </>
            );
          })()}
          {item.code?.trim() && (
            <div className="product-code" dir="ltr">{item.code.trim()}</div>
          )}
          <div className="product-price">
            {p.price > 0 ? fmt(p.price) : '—'}
            {fellBack && p.price > 0 && (
              <span
                className="price-fallback-dot"
                title={t({
                  en: 'No price set for this source — showing the default price',
                  ar: 'لا يوجد سعر لهذا المصدر — يظهر السعر الافتراضي',
                })}
              >
                *
              </span>
            )}
          </div>
          {unitLabel && (
            <div className="product-unit">
              {t({ en: 'Unit', ar: 'الوحدة' })}: {unitLabel}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="catalog-area">
      {/* Search bar */}
      <div className="search-bar">
        <div className="search-input-wrap">
          <SearchIcon className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder={t({ en: 'Scan barcode or search items…', ar: 'امسح باركود او ابحث عن صنف' })}
            value={search}
            onChange={(e) => {
              const now = performance.now();
              const v = e.target.value;
              const tr = typing.current;
              // One character more than last time, within a scanner's gap,
              // keeps the burst going; anything else (a paste, a deletion, a
              // human pause) is not a scanner.
              tr.machine = v.length <= 1
                ? true
                : tr.machine && v.length === tr.len + 1 && now - tr.at < SCANNER_GAP_MS;
              tr.at = now;
              tr.len = v.length;
              setSearch(v);
            }}
            onKeyDown={(e) => {
              // A scanner's Enter/Tab suffix: ring it up now, without waiting
              // for the settle window. Nothing resolvable → leave the query.
              if (e.key === 'Enter' || e.key === 'Tab') {
                if (commitSearch()) e.preventDefault();
              }
            }}
          />
        </div>
        <button
          className="btn-icon-44"
          title={t({ en: 'Scan barcode', ar: 'مسح الباركود' })}
          aria-label={t({ en: 'Scan barcode', ar: 'مسح الباركود' })}
          onClick={() => setScannerOpen(true)}
        >
          <BarcodeIcon size={20} />
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            // Same behavior as the cart's customer pill: opening the
            // picker pre-selects the current customer when one is
            // attached so the cashier can review or replace from inside.
            if (customer) {
              useCustomerPicker.getState().setSourceType(customer.type);
              useCustomerPicker.getState().selectId(customer.id);
            }
            showModal('customerPicker');
          }}
        >
          <UserIcon size={16} />
          {customer ? (customer.name?.split(' ')[0] ?? `#${customer.id}`) : t({ en: 'Customer', ar: 'العميل' })}
        </button>
      </div>

      {/*
        Price list (invoice source). Rendered ONLY when the branch actually has
        sources configured — on every other tenant this row does not exist, so
        the screen is unchanged for them.

        Disabled while an order is open for editing: the lines carry the prices
        they were sold at, and re-tiering them mid-edit would restate a receipt
        the customer already holds. The legacy POS disables its dropdown in the
        same situation.
      */}
      {sources.length > 0 && (
        <div className="source-bar">
          <label className="source-bar-label" htmlFor="invoice-source-select">
            {t({ en: 'Price list', ar: 'قائمة الأسعار' })}
          </label>
          <select
            id="invoice-source-select"
            className="source-select"
            value={activeSourceId ?? ''}
            disabled={sourceLocked}
            onChange={(e) => pickSource(Number(e.target.value))}
          >
            {/*
              Only rendered when the till is on no source at all — which is the
              edit-mode case, where the receipt predates price lists and has a
              NULL SourceId. A live sale always has one selected, so this
              placeholder never appears as a choosable option there.
            */}
            {activeSourceId == null && (
              <option value="">{t({ en: '—', ar: '—' })}</option>
            )}
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {lang === 'ar' ? s.name_ar || s.name : s.name || s.name_ar}
              </option>
            ))}
          </select>
          {sourceLocked && (
            <span className="source-bar-note">
              {t({ en: 'Locked while editing', ar: 'مقفل أثناء التعديل' })}
            </span>
          )}
        </div>
      )}

      {/* Categories — chips wrap onto multiple rows, no sideways scrolling */}
      <div className="categories">
        {/* Back: its own style (soft primary, bold, real arrow that turns
            with the reading direction) and the path the cashier is in, so it
            never reads as one more category. */}
        {parentStack.length > 0 && (
          <button className="cat-btn cat-back" onClick={goBackUpStack}>
            <span className="cat-back-arrow">
              <ArrowRightIcon size={18} />
            </span>
            <span>{t({ en: 'Back', ar: 'رجوع' })}</span>
            <span className="cat-back-path">{parentStack.map((p) => catName(p)).join(' › ')}</span>
          </button>
        )}

        {allCatsQuery.isLoading && (
          <button className="cat-btn" disabled>
            <span className="cat-icon">⏳</span>
            {t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}
          </button>
        )}

        {visibleCategories.map((c) => {
          const v = categoryVisual(c);
          const isActive = c.id === activeLeafId;
          const childCount = allCats.filter((x) => x.parent_id === c.id).length;
          return (
            <button
              key={c.id}
              className={`cat-btn ${childCount > 0 ? 'cat-parent' : ''} ${isActive ? 'active' : ''}`}
              onClick={() => handleCategoryClick(c)}
              title={childCount > 0 ? `${childCount} ${t({ en: 'sub-categories', ar: 'فئات فرعية' })}` : undefined}
            >
              {/* The admin's own photo wins over the emoji guess. It is only
                  ever a thumbnail here — the chip is a label, not a tile — so
                  the same 500×500 upload the QR menu shows is fine at 26px. */}
              {c.image_data ? (
                <img className="cat-photo" src={c.image_data} alt="" aria-hidden="true" />
              ) : (
                <span className="cat-icon">
                  {v.icon ? v.icon : <PackageIcon size={18} />}
                </span>
              )}
              <span>{catName(c, c.code)}</span>
              {/* A parent opens more categories, not items: say how many, and
                  point the way in. */}
              {childCount > 0 && (
                <>
                  <span className="cat-sub-count">{childCount}</span>
                  <span className="cat-chevron">
                    <ChevronRightIcon size={16} />
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Product grid */}
      <div className="products">
        {isSearching ? (
          /* Global search — results across ALL categories. */
          <>
            <div className="products-section-label">
              {shownResults.length > 0
                ? `🔎 ${t({ en: 'Results for', ar: 'نتائج البحث عن' })} "${shownQuery.trim()}"`
                : `${t({ en: 'No items match', ar: 'لا توجد أصناف تطابق' })} "${shownQuery.trim()}"`}
              {shownResults.length > SEARCH_RENDER_CAP &&
                ` · ${t({
                  en: `showing ${SEARCH_RENDER_CAP} of ${shownResults.length}, keep typing to narrow`,
                  ar: `يظهر ${SEARCH_RENDER_CAP} من ${shownResults.length}، تابع الكتابة للتضييق`,
                })}`}
            </div>
            {shownResults
              .slice(0, SEARCH_RENDER_CAP)
              .map((item) => renderTile(item, itemContext(item), 'search-'))}
          </>
        ) : (
          <>
            {/* Featured items — landing page (top level, no leaf, no search). */}
            {showFeatured && (
              <>
                <div className="products-section-label">⭐ {t({ en: 'Featured', ar: 'المميزة' })}</div>
                {featuredItems.map((item) => renderTile(item, itemContext(item), 'feat-'))}
              </>
            )}

            {activeLeafId === null && visibleCategories.length > 0 && !showFeatured && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  padding: 40,
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 14,
                }}
              >
                {currentParent
                  ? `${t({ en: 'Browsing', ar: 'تصفح' })} ${catName(currentParent)} — ${t({ en: 'tap a sub-category to view items', ar: 'اضغط فئة فرعية لعرض الأصناف' })}`
                  : t({ en: 'Tap a category to view items', ar: 'اضغط على فئة لعرض الأصناف' })}
              </div>
            )}

            {itemsQuery.isLoading && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  padding: 40,
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                }}
              >
                {t({ en: 'Loading items…', ar: 'جارٍ تحميل الأصناف…' })}
              </div>
            )}

            {activeLeafId !== null && itemsQuery.isSuccess && categoryItems.length === 0 && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  padding: 40,
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 13,
                }}
              >
                {t({ en: 'No items in this category', ar: 'لا توجد أصناف في هذه الفئة' })}
              </div>
            )}

            {categoryItems.map((item) =>
              renderTile(item, { visual: activeVisual, station: activeCategoryName }),
            )}
          </>
        )}
      </div>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        lookup={scanLookup}
        onAdd={handleScanAdd}
        onSearch={(code) => setSearch(code)}
      />
    </section>
  );
}
